import React, { useRef, useEffect, useCallback, useState } from 'react';
import { PitchResult } from '../utils/pitchDetection';
import { VoiceType, VOICE_LABELS, buildVoice, releaseVoice, VoiceNodes } from '../utils/synth';
import './Piano.css';

export type { VoiceType };
export { VOICE_LABELS };

interface Props {
  currentPitch: PitchResult | null;
  voice?: VoiceType;
  onHeldKeysChange?: (keys: ReadonlySet<number>) => void;
  onNoteOn?: (midi: number, timeMs: number) => void;
  onNoteOff?: (midi: number, timeMs: number) => void;
  onHoverChange?: (midi: number | null) => void;
}

const BLACK_NOTE_INDICES = new Set([1, 3, 6, 8, 10]);
const MIN_MIDI = 36;
const MAX_MIDI = 95;

// Standard piano keyboard mapping — centred on octave 4
// Bottom row white keys: Z X C V B N M , .
// Bottom row black keys: S D   G H J
// Upper row white keys:  Q W E R T Y U I O P
// Upper row black keys:  2 3   5 6 7
const KEY_MAP: Record<string, number> = {
  // Bottom row (octave 4 white keys)
  KeyZ: 60, KeyX: 62, KeyC: 64, KeyV: 65, KeyB: 67, KeyN: 69, KeyM: 71, Comma: 72, Period: 74,
  // Bottom row (octave 4 black keys)
  KeyS: 61, KeyD: 63, KeyG: 66, KeyH: 68, KeyJ: 70,
  // Top row (octave 3 white keys)
  KeyQ: 48, KeyW: 50, KeyE: 52, KeyR: 53, KeyT: 55, KeyY: 57, KeyU: 59, KeyI: 60, KeyO: 62, KeyP: 64,
  // Top row (octave 3 black keys)
  Digit2: 49, Digit3: 51, Digit5: 54, Digit6: 56, Digit7: 58,
};

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const midiToNoteName = (midi: number) => `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;

// Which keyboard key label to show on each piano key (for visual hint)
const KEY_LABEL: Record<number, string> = {
  60: 'Z', 62: 'X', 64: 'C', 65: 'V', 67: 'B', 69: 'N', 71: 'M', 72: ',', 74: '.',
  61: 'S', 63: 'D', 66: 'G', 68: 'H', 70: 'J',
  48: 'Q', 50: 'W', 52: 'E', 53: 'R', 55: 'T', 57: 'Y', 59: 'U',
  49: '2', 51: '3', 54: '5', 56: '6', 58: '7',
};

interface Key { midi: number; x: number; isBlack: boolean; label?: string; }

const KEYS: Key[] = (() => {
  const keys: Key[] = [];
  let whiteIdx = 0;
  for (let m = MIN_MIDI; m <= MAX_MIDI; m++) {
    const noteIdx = m % 12;
    const isBlack = BLACK_NOTE_INDICES.has(noteIdx);
    const octave = Math.floor(m / 12) - 1;
    const label = noteIdx === 0 ? `C${octave}` : undefined;
    if (isBlack) {
      keys.push({ midi: m, x: (whiteIdx - 1) + 0.65, isBlack, label });
    } else {
      keys.push({ midi: m, x: whiteIdx, isBlack, label });
      whiteIdx++;
    }
  }
  return keys;
})();

const TOTAL_WHITES = KEYS.filter(k => !k.isBlack).length;

export default function Piano({ currentPitch, voice = 'sine', onHeldKeysChange, onNoteOn, onNoteOff, onHoverChange }: Props) {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const activeRef = useRef<Map<number, VoiceNodes>>(new Map());
  const [heldKeys, setHeldKeys] = useState<ReadonlySet<number>>(new Set());
  const [hoveredKey, setHoveredKey] = useState<number | null>(null);

  const hoverKey = useCallback((midi: number | null) => {
    setHoveredKey(midi);
    onHoverChange?.(midi);
  }, [onHoverChange]);
  const voiceRef = useRef(voice);
  useEffect(() => { voiceRef.current = voice; }, [voice]);

  const currentMidi = currentPitch
    ? Math.round(12 * Math.log2(currentPitch.hz / 440) + 69)
    : null;

  const ensureCtx = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  };

  const startNote = useCallback((midi: number) => {
    if (activeRef.current.has(midi)) return;
    const ctx = ensureCtx();
    const nodes = buildVoice(ctx, midi, voiceRef.current, ctx.destination, ctx.currentTime);
    activeRef.current.set(midi, nodes);
    setHeldKeys(prev => { const s = new Set(prev); s.add(midi); return s; });
    onNoteOn?.(midi, performance.now());
  }, [onNoteOn]);

  const stopNote = useCallback((midi: number) => {
    const node = activeRef.current.get(midi);
    if (!node || !audioCtxRef.current) return;
    activeRef.current.delete(midi);
    setHeldKeys(prev => { const s = new Set(prev); s.delete(midi); return s; });
    onNoteOff?.(midi, performance.now());
    releaseVoice(audioCtxRef.current, node, voiceRef.current);
  }, [onNoteOff]);

  const stopAllNotes = useCallback(() => {
    Array.from(activeRef.current.keys()).forEach(midi => stopNote(midi));
  }, [stopNote]);

  useEffect(() => {
    onHeldKeysChange?.(heldKeys);
  }, [heldKeys, onHeldKeysChange]);

  // Keyboard piano support
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.repeat) return;
      const midi = KEY_MAP[e.code];
      if (midi !== undefined) { e.preventDefault(); startNote(midi); }
    };
    const up = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const midi = KEY_MAP[e.code];
      if (midi !== undefined) stopNote(midi);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [startNote, stopNote]);

  // Global mouseup/touchend release
  useEffect(() => {
    const up = () => stopAllNotes();
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => { window.removeEventListener('mouseup', up); window.removeEventListener('touchend', up); };
  }, [stopAllNotes]);

  useEffect(() => () => { stopAllNotes(); audioCtxRef.current?.close(); }, []);

  const W = TOTAL_WHITES;
  const H = 10;

  return (
    <div className="piano">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {KEYS.filter(k => !k.isBlack).map(k => {
          const isCurrent = currentMidi === k.midi;
          const isHeld = heldKeys.has(k.midi);
          const isHovered = hoveredKey === k.midi;
          const kbLabel = KEY_LABEL[k.midi];
          return (
            <g
              key={k.midi}
              className={`key white${isCurrent ? ' current' : ''}${isHeld ? ' held' : ''}${isHovered ? ' hovered' : ''}`}
              onMouseDown={() => startNote(k.midi)}
              onMouseEnter={(e) => { hoverKey(k.midi); if (e.buttons === 1) startNote(k.midi); }}
              onMouseLeave={() => { hoverKey(null); stopNote(k.midi); }}
              onTouchStart={(e) => { e.preventDefault(); startNote(k.midi); }}
            >
              <title>{midiToNoteName(k.midi)}{kbLabel ? ` [${kbLabel}]` : ''}</title>
              <rect x={k.x + 0.04} y={0} width={0.92} height={H} rx={0.1} />
              {isHovered && (
                <text x={k.x + 0.5} y={1.5} textAnchor="middle" fontSize="1.25" fontFamily="monospace"
                      className="hover-note">
                  {midiToNoteName(k.midi)}
                </text>
              )}
              {kbLabel && (
                <text x={k.x + 0.5} y={H - 1.2} textAnchor="middle" fontSize="1.4" fontFamily="monospace"
                      className="kb-hint">
                  {kbLabel}
                </text>
              )}
            </g>
          );
        })}
        {KEYS.filter(k => k.isBlack).map(k => {
          const isCurrent = currentMidi === k.midi;
          const isHeld = heldKeys.has(k.midi);
          const isHovered = hoveredKey === k.midi;
          const kbLabel = KEY_LABEL[k.midi];
          return (
            <g
              key={k.midi}
              className={`key black${isCurrent ? ' current' : ''}${isHeld ? ' held' : ''}${isHovered ? ' hovered' : ''}`}
              onMouseDown={() => startNote(k.midi)}
              onMouseEnter={(e) => { hoverKey(k.midi); if (e.buttons === 1) startNote(k.midi); }}
              onMouseLeave={() => { hoverKey(null); stopNote(k.midi); }}
              onTouchStart={(e) => { e.preventDefault(); startNote(k.midi); }}
            >
              <title>{midiToNoteName(k.midi)}{kbLabel ? ` [${kbLabel}]` : ''}</title>
              <rect x={k.x} y={0} width={0.6} height={H * 0.6} rx={0.08} />
              {isHovered && (
                <text x={k.x + 0.3} y={1.1} textAnchor="middle" fontSize="0.85"
                      fontFamily="monospace" className="hover-note black">
                  {midiToNoteName(k.midi)}
                </text>
              )}
              {kbLabel && (
                <text x={k.x + 0.3} y={H * 0.6 - 0.5} textAnchor="middle" fontSize="1.0"
                      fontFamily="monospace" className="kb-hint">
                  {kbLabel}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
